import HBMessage from "@/components/HBMessage";
import { getMe, getWxLoginRes, getWxQrcode } from "@/services";
import base64 from "@/utils/base64";
import { isSpiderClient } from "@/utils/isSpider";
import { MSG_TYPE } from "@/utils/postMessageToParent";
import { CLIENT_API, env } from "@/utils/request";
import useRequest from "@/utils/useReq";
import { useLocalStorageState, useMemoizedFn } from "ahooks";
import QRCode from "qrcode.react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "../index.module.less";
import { isDestoryingAccount, openDestoryAccountInfo } from "../loginErrHandle";
import accountLocked from "./accountLocked";

function isExpired(time) {
  // 9分钟
  return Date.now() - time > 9 * 60 * 1000;
}

const pollingLoginRes = async (wxQrcodeData, onLogin, refresh, timeRef) => {
  // 避免多次调用产生多个轮询
  if (timeRef.current) {
    clearTimeout(timeRef.current);
  }

  timeRef.current = setTimeout(async () => {
    // tab隐藏时避免发请求浪费资源
    if (document.visibilityState === "hidden") {
      pollingLoginRes(wxQrcodeData, onLogin, refresh, timeRef);
      return;
    }

    // 实时取，避免另一个tab登录导致二维码过期失效
    // const wxQrcodeData = JSON.parse(
    //   localStorage.getItem("huaban_wx_login_cache_data") || "{}"
    // );

    if (wxQrcodeData.data) {
      if (isExpired(wxQrcodeData.time)) {
        refresh();
        return;
      }

      const res = await getWxLoginRes(wxQrcodeData.data.scene_str);

      // 账号注销
      if (isDestoryingAccount(res)) {
        openDestoryAccountInfo();
        refresh(true);
        return;
      }

      // 账号冻结处理
      if (res.err === 403) {
        accountLocked(res);
        refresh(true);
        return;
      }

      if (res.err) {
        refresh(true);
        return;
      }

      if (Object.keys(res).length > 0) {
        onLogin(res);
      } else {
        pollingLoginRes(wxQrcodeData, onLogin, refresh, timeRef);
      }
    }
  }, 3000);
};

/**
 * 微信登录二维码
 */
export const WxLogin = forwardRef(function WxLoginRaw(
  { size = 156, style, onLogin },
  ref
) {
  const timeRef = useRef(null);

  const [wxQrcodeData, setWxQrcodeData] = useLocalStorageState(
    "huaban_wx_login_cache_data",
    {
      defaultValue: {},
    }
  );
  const [err, setErr] = useState();

  const { run: refresh } = useRequest(async (force) => {
    if (!wxQrcodeData.data || isExpired(wxQrcodeData.time) || force) {
      const res = await getWxQrcode();
      if (res.err) {
        setErr("获取二维码失败, 请刷新页面重试");
        return false;
      }

      setErr(false);
      setWxQrcodeData({
        data: res,
        // 二维码 10分钟过期
        time: Date.now(),
      });
      return res;
    }
  });

  useImperativeHandle(ref, () => ({
    refresh: refresh,
  }));

  const loginCb = useCallback(
    (res) => {
      onLogin(res);
      setWxQrcodeData({});
    },
    [onLogin, setWxQrcodeData]
  );

  useEffect(() => {
    if (wxQrcodeData.data) {
      pollingLoginRes(wxQrcodeData, loginCb, refresh, timeRef);
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timeRef.current);
      };
    }
  }, [loginCb, onLogin, refresh, setWxQrcodeData, wxQrcodeData]);

  return (
    <div
      style={{
        ...style,
      }}
    >
      <div
        className={styles.qrcodeWrap}
        style={{
          width: size,
          height: size,
        }}
      >
        {err ? (
          <div className={styles.err}>
            <span>
              获取二维码失败,
              <br />
              请点击 <a onClick={refresh}>重试</a>
            </span>
          </div>
        ) : (
          wxQrcodeData.data && (
            <QRCode
              id="wx_login"
              value={wxQrcodeData.data.url}
              size={size} // 二维码的大小
              fgColor="#000000" // 二维码的颜色
              style={{ margin: "auto" }}
            />
          )
        )}
      </div>
    </div>
  );
});

/**
 * iframe形式嵌套微信二维码，此种方式不用关注公众号
 * @param {{
 *    size: number;
 *    onLogin: (data: {})=> void;
 *    style:any;
 * }}
 */
function IframeWxQrcodeRaw({ size = 156, onLogin, style }, ref) {
  /**自定义微信二维码样式 */
  const customStyle = useMemo(() => {
    // 微信iframe的二维码是自带30px的内边距的，所示计算展示尺寸时，需要进行换算一下，减去内边距的影响
    const padding = (30 * size) / 410;
    const tranfromSize = size + padding * 2;

    const style = `.impowerBox .qrcode {width: ${tranfromSize}px;height: ${tranfromSize}px; margin-top: -${padding}px;border: none; margin-left: -${padding}px;}
.impowerBox .title {display: none;}
.impowerBox .info {display: none;}
.status_icon {display: none}
.impowerBox .status {text-align: center;}`;

    // 转base64
    return `data:text/css;base64,${base64.encode(style)}`;
  }, [size]);

  // 时间戳，用于更新链接，刷新二维码
  const [timestamp, setTimestamp] = useState(Date.now());
  const refresh = useMemoizedFn(() => setTimestamp(Date.now()));

  useImperativeHandle(ref, () => ({
    refresh: refresh,
  }));

  useEffect(() => {
    const callback = async (e) => {
      // 非微信的iframe嵌套页面message消息，不做响应
      if (e.data?.isIframe === false) {
        return;
      }

      if (e.data?.type === MSG_TYPE.thirdLoginOk) {
        HBMessage.success("操作成功");
        const user = await getMe();
        onLogin({ user });
      }

      // 注册场景，iframe里跳转到signup页面，然后通知注册场景
      if (e.data?.type === MSG_TYPE.thirdRegister) {
        HBMessage.success("操作成功");
        onLogin();
      }

      if (e.data?.type === MSG_TYPE.thirdLoginFail) {
        if (isDestoryingAccount(e.data)) {
          openDestoryAccountInfo({
            onOk: () => {
              refresh();
            },
          });
          return;
        }

        HBMessage.error(e.data.msg || "登录失败，请重试");
        refresh();
      }
    };
    window.addEventListener("message", callback);

    return () => {
      window.removeEventListener("message", callback);
    };
  }, [onLogin, refresh]);

  const [url, setUrl] = useState();

  // 异步加载微信二维码
  useEffect(() => {
    // 获取后台地址
    const baseUrl = CLIENT_API[env];

    if (isSpiderClient()) {
      return setUrl(null);
    }

    // return `https://open.weixin.qq.com/connect/qrconnect?appid=wxadc3342faa49acef&response_type=code&redirect_uri=https%3A%2F%2Fhuaban.com%2Foauth%2Fcallback%2F&scope=snsapi_login&state=iqHr98&href=${customStyle}&self_redirect=true#wechat_redirect`;
    setUrl(
      baseUrl +
        `/oauth/wechat/login/?self_redirect=true&href=${customStyle}&timestamp=${timestamp}&fast_login=0`
    );
  }, [customStyle, timestamp]);

  if (!url) {
    return null;
  }

  return (
    <iframe
      width={size}
      height={size}
      frameBorder={0}
      scrolling="no"
      src={url}
      style={style || {}}
    />
  );
}

export const IframeWxQrcode = forwardRef(IframeWxQrcodeRaw);
